import { candleDirection } from "../lib/resolve";
import type { LoopState } from "../state/roundLoop";
import type { BinaryLabel, Candle, Setup } from "../types";
import type { OverlayLine } from "./Chart";
import type { BinaryChoicePair } from "./GuessControls";
import { GuessControls } from "./GuessControls";
import { HistoryPanels, HistoryToggles } from "./HistoryPanel";
import { LoopShell, type PlayableRound } from "./LoopShell";
import { NoteField } from "./NoteField";
import { PredictWeeklyBanner } from "./PredictWeeklyBanner";

/**
 * The Predict Next-Candle Loop (spec §7): read the Context, call the very next
 * candle green or red, reveal it. One component for all three timeframes.
 * Every bank is a barrier bank (ADR-0013), so the answer is the Round's first
 * Forward candle everywhere; the accessor stays a prop so a Loop with some
 * other notion of "the next candle" could still use this view.
 *
 * The whole stored Context is charted: the Context-length setting belongs to
 * the trend game and is inert here (see loops.ts `NEXT_CANDLE_SETTINGS`).
 *
 * GRA-124: at reveal, "Decision" marks the last Context candle. There's no
 * barrier walk here, so unlike the trend Loop the single answer candle
 * always sits exactly one candle past the decision point, and is charted
 * plainly enough that it doesn't need a marker of its own.
 */

const GREEN_RED: BinaryChoicePair = {
  up: { text: "Green", letterKey: "g" },
  down: { text: "Red", letterKey: "r" },
};

/** A Round any Next-Candle Loop can play: reveal metadata plus a Context. */
export type NextCandleRound = PlayableRound & {
  context: Candle[];
  dateRange: { start: string; end: string };
  /** The Context's detected setup (GRA-141), stamped by the pipeline. */
  setup?: Setup;
};

export function NextCandleView<R extends NextCandleRound>({
  store,
  loadingMessage,
  answerCandle,
  markets = "none",
}: {
  store: <T>(selector: (state: LoopState<R, BinaryLabel, null>) => T) => T;
  loadingMessage: string;
  /** The Round's answer candle: `forward[0]` on every bank today. */
  answerCandle: (round: R) => Candle;
  /** Whether the settings strip offers this Loop's bank as a market filter. */
  markets?: "from-bank" | "none";
}) {
  // Read directly for the reveal markers below; LoopShell re-selects the same
  // field to drive the rest of its own render, so this doesn't add a second
  // source of truth, just a second read of one.
  const current = store((s) => s.current);

  // "Decision" at the last Context candle (the whole Context is charted
  // here, unlike the trend Loop's sliceable window, so the decision point is
  // always the Context's own last index). Shown only once revealed, matching
  // the trend Loop's markers (GRA-124).
  const revealMarkers = (revealed: boolean): OverlayLine[] => {
    if (!revealed || !current) return [];
    const decisionIndex = current.context.length - 1;
    return [{ dataIndex: decisionIndex, tone: "muted", label: "Decision", anchor: "top" }];
  };

  return (
    <LoopShell
      store={store}
      loadingMessage={loadingMessage}
      markets={markets}
      chartContext={(round) => round.context}
      chartForward={(round, revealed) => (revealed ? [answerCandle(round)] : [])}
      overlays={({ revealed }) => revealMarkers(revealed)}
      // The Forward here is always exactly the one answer candle (GRA-147):
      // the reveal opens fully drawn, same as it always did, there's just no
      // "before the answer" position worth stepping back to.
      resolutionStep={() => 1}
      historyToggles={<HistoryToggles />}
      historyPanels={<HistoryPanels />}
      footer={({ round, commit, revealed, submit, next, note = "", setNote = () => {} }) =>
        revealed && commit ? (
          <PredictWeeklyBanner
            guess={commit}
            answer={candleDirection(answerCandle(round))}
            note={note}
            setup={round.setup}
            onNext={next}
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <GuessControls onGuess={submit} binaryPair={GREEN_RED} disabled={revealed} />
            <NoteField value={note} onChange={setNote} disabled={revealed} />
          </div>
        )
      }
    />
  );
}
